// MaturaRightTriangleCircumradiusClosedTask.js
// Zadanie zamknięte: promień okręgu opisanego na trójkącie prostokątnym
// Dla trójkąta prostokątnego: R = c / 2, gdzie c – przeciwprostokątna.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";
import TriangleDiagram from "../../../../components/diagrams/TriangleDiagram";

/* ============================================================================
   1. PITAGOREJSKIE TRÓJKI – BAZA
   ========================================================================== */

const BASE_TRIPLES = [
  { a: 3, b: 4, c: 5, scales: [1, 2, 3, 4, 5] },
  { a: 5, b: 12, c: 13, scales: [1, 2, 3] },
  { a: 8, b: 15, c: 17, scales: [1, 2] },
  { a: 7, b: 24, c: 25, scales: [1, 2] },
];

function buildAllTriples() {
  const out = [];
  BASE_TRIPLES.forEach(({ a, b, c, scales }) => {
    scales.forEach((k) => {
      out.push({ a: a * k, b: b * k, c: c * k });
    });
  });
  return out;
}

const ALL_TRIPLES = buildAllTriples();

/* ============================================================================
   2. UŁAMKI
   ========================================================================== */

function gcd(x, y) {
  let a = Math.abs(x);
  let b = Math.abs(y);
  while (b) {
    const t = b;
    b = a % b;
    a = t;
  }
  return a || 1;
}

function makeFrac(n, d) {
  const g = gcd(n, d);
  return { n: n / g, d: d / g };
}

function latexFrac(fr) {
  if (fr.d === 1) return `${fr.n}`;
  return `\\frac{${fr.n}}{${fr.d}}`;
}

function equalFrac(f1, f2) {
  return f1.n * f2.d === f2.n * f1.d;
}

/* ============================================================================
   3. GENEROWANIE OPCJI A–D
   ========================================================================== */

export function buildOptions(a, b, c, { shuffle = true } = {}) {
  // Gdy losowanie trafi dokładnie w dane z czerwca 2025, zachowujemy również
  // odpowiedzi CKE. Nie tworzymy „prawie oryginału” z innymi dystraktorami.
  if (a === 10 && b === 24 && c === 26) {
    return {
      options: ["\\frac{80}{13}", "\\frac{20}{3}", "12", "13"],
      correctIndex: 3,
    };
  }

  // poprawny promień okręgu opisanego: R = c / 2
  const correct = makeFrac(c, 2);

  // Każdy dystraktor odpowiada konkretnemu, typowemu błędowi:
  const cand = [
    makeFrac(Math.max(a, b), 2), // podzielenie przez 2 niewłaściwego boku
    makeFrac(a + b - c, 2), // pomylenie promienia opisanego z wpisanym
    makeFrac(c, 1), // pomylenie promienia ze średnicą
  ];

  // filtrujemy duplikaty i poprawną wartość
  const wrong = [];
  for (const fr of cand) {
    if (equalFrac(fr, correct)) continue;
    if (wrong.some((w) => equalFrac(w, fr))) continue;
    wrong.push(fr);
    if (wrong.length === 3) break;
  }

  // Awaryjnie zachowujemy cztery unikalne odpowiedzi także dla nowych trójek.
  let extra = 1;
  while (wrong.length < 3) {
    const fr = makeFrac(correct.n + extra * correct.d, correct.d);
    if (!equalFrac(fr, correct) && !wrong.some((w) => equalFrac(w, fr))) {
      wrong.push(fr);
    }
    extra++;
  }

  const all = [correct, ...wrong];

  // tasowanie Fisher–Yates
  if (shuffle) {
    for (let i = all.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [all[i], all[j]] = [all[j], all[i]];
    }
  }

  const correctIndex = all.findIndex((fr) => equalFrac(fr, correct));

  return {
    options: all.map(latexFrac),
    correctIndex,
  };
}

/* ============================================================================
   4. GENERATE TASK
   ========================================================================== */

/**
 * Wariant „oryginał” – matura podstawowa, czerwiec 2025, zadanie 19.2:
 *   trójkąt prostokątny 10, 24, 26;  R = 26/2 = 13  (D).
 */
function createJune2025OriginalTask() {
  return {
    type: "MaturaRightTriangleCircumradiusClosedTask",
    data: {
      a: 10,
      b: 24,
      c: 26,
      options: ["\\frac{80}{13}", "\\frac{20}{3}", "12", "13"],
      correctIndex: 3,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_CZE",
      taskNumber: "19.2",
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_CZE"
  ) {
    return createJune2025OriginalTask();
  }

  const triple =
    ALL_TRIPLES[Math.floor(Math.random() * ALL_TRIPLES.length)];
  const { a, b, c } = triple;

  const { options, correctIndex } = buildOptions(a, b, c);

  return {
    type: "MaturaRightTriangleCircumradiusClosedTask",
    data: {
      a,
      b,
      c,
      options, // latex stringi
      correctIndex,
    },
  };
}

/* ============================================================================
   5. RENDER TASK
   ========================================================================== */

export function renderTask({ data }) {
  const { a, b, c, options } = data;

  return (
    <div style={{ textAlign: "left" }} className="space-y-3">
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>

      <p>
        Dany jest trójkąt prostokątny o bokach długości{" "}
        <InlineMath math={`${a}`} />,{" "}
        <InlineMath math={`${b}`} /> oraz{" "}
        <InlineMath math={`${c}`} /> (zobacz rysunek).
      </p>

      <TriangleDiagram
        a={a}
        b={b}
        angleAt="A"
        showSideLabels={true}
        showBetaAngle={false}
      />

      <p>
        Długość promienia okręgu opisanego na tym trójkącie jest równa:
      </p>

      <div style={{ marginTop: 8 }}>
        {options.map((latex, idx) => {
          const letter = ["A", "B", "C", "D"][idx];
          return (
            <div key={letter} style={{ marginBottom: 6 }}>
              <InlineMath math={`${letter}.\\; ${latex}`} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================================
   6. RENDER INPUT (ABCD)
   ========================================================================== */

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = data.options.map((_, idx) => ({
    value: idx,
    label: ["A", "B", "C", "D"][idx],
    textNode: ["A", "B", "C", "D"][idx],
  }));

  const handleChange = (idx) => {
    const letter = ["A", "B", "C", "D"][idx];
    const latex = data.options[idx];

    onChange?.({
      choiceIndex: idx,
      label: letter,
      latex,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

/* ============================================================================
   7. TWOJA ODPOWIEDŹ
   ========================================================================== */

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    userAnswer.choiceIndex < 0 ||
    userAnswer.choiceIndex > 3
  ) {
    return <div>Twoja odpowiedź: (brak odpowiedzi)</div>;
  }

  const { label, latex } = userAnswer;

  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

/* ============================================================================
   8. SPRAWDZANIE
   ========================================================================== */

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") {
    return false;
  }
  return userAnswer.choiceIndex === data.correctIndex;
}

/* ============================================================================
   9. POPRAWNA ODPOWIEDŹ
   ========================================================================== */

export function renderAnswer({ data }) {
  const letter = ["A", "B", "C", "D"][data.correctIndex];
  const latex = data.options[data.correctIndex];

  return (
    <div>
      <span>Poprawna odpowiedź: </span>
      <InlineMath math={`${letter}.\\; ${latex}`} />
    </div>
  );
}

/* ============================================================================
   10. WYJAŚNIENIE
   ========================================================================== */

export function renderExplanation({ data }) {
  const { c } = data;

  const Rnum = c;
  const Rden = 2;

  const Rlatex = Rnum % 2 === 0 ? String(Rnum / 2) : `\\dfrac{${Rnum}}{2}`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1.</strong> W trójkącie prostokątnym promień okręgu opisanego jest równy
        połowie przeciwprostokątnej:
      </p>

      <BlockMath math={'R = \\dfrac{c}{2}'} />

      <p>
        <strong>Krok 2.</strong> W tym zadaniu przeciwprostokątna ma długość{" "}
        <InlineMath math={`c=${c}`} />, więc:
      </p>

      <BlockMath math={`R = \\dfrac{${c}}{2}`} />

      <p>
        <strong>Krok 3.</strong> Obliczamy:
      </p>

      <BlockMath math={`R = ${Rlatex}`} />
    </div>
  );
}


/* ============================================================================
   11. EXPORT DEFAULT
   ========================================================================== */

export default {
  generateTask,
  renderTask,
  renderInput,
  renderUserAnswer,
  checkAnswer,
  renderAnswer,
  renderExplanation,
};
